import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import {
  Building2,
  AlertTriangle,
  Calendar,
  BarChart3,
  Users,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Skeleton } from '@/app/components/ui/skeleton';
import { useConsultantOverview } from '@/hooks/use-management-reviews';
import { useSwitchOrg } from '@/hooks/use-memberships';
import type { ConsultantOrgSummary } from '@/lib/types';
import { formatDateOnly } from '@/lib/date-utils';

function ScoreRing({ score }: { score: number }) {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color =
    score >= 80 ? 'text-green-500' : score >= 50 ? 'text-yellow-500' : 'text-red-500';
  const stroke =
    score >= 80 ? 'stroke-green-500' : score >= 50 ? 'stroke-yellow-500' : 'stroke-red-500';

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="52" height="52" className="-rotate-90">
        <circle
          cx="26"
          cy="26"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          className="text-gray-200"
        />
        <circle
          cx="26"
          cy="26"
          r={radius}
          fill="none"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={stroke}
        />
      </svg>
      <span className={`absolute text-xs font-bold ${color}`}>{score}%</span>
    </div>
  );
}

function OrgCard({ org, onSwitchOrg }: { org: ConsultantOrgSummary; onSwitchOrg: (orgId: number) => void }) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          <ScoreRing score={org.compliance_score} />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{org.organization_name}</h3>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {org.critical_alerts > 0 && (
                <Badge className="bg-red-100 text-red-800">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {org.critical_alerts} critical
                </Badge>
              )}
              {org.next_audit_date && (
                <Badge variant="outline" className="text-xs">
                  <Calendar className="h-3 w-3 mr-1" />
                  Audit: {formatDateOnly(org.next_audit_date)}
                </Badge>
              )}
              {org.latest_quarter_stats_status && (
                <Badge variant="outline" className="text-xs">
                  Q: {org.latest_quarter_stats_status}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSwitchOrg(org.organization_id)}
          >
            View Dashboard
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function ConsultantPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { data, isLoading, error } = useConsultantOverview();
  const switchOrgMutation = useSwitchOrg();

  const handleSwitchOrg = (orgId: number) => {
    switchOrgMutation.mutate(orgId, {
      onSuccess: async () => {
        // Refresh the auth context with the new tenant before navigating so
        // the destination page does not mount queries that get wiped by the
        // org-change detection in refreshUser.
        await refreshUser();
        navigate('/compliance');
      },
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  if (error || (data && 'error' in data)) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium">Consultant View Unavailable</p>
            <p className="text-sm text-gray-500 mt-1">
              This view is only available to users who belong to multiple organizations.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { summary, organizations } = data;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Consultant Portal
        </h1>
        <p className="text-gray-600 mt-1">
          Multi-organization compliance overview
        </p>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{summary.total_clients}</p>
                <p className="text-sm text-gray-600">Client Organizations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{summary.average_compliance_score}%</p>
                <p className="text-sm text-gray-600">Avg Compliance Score</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={summary.total_critical_alerts > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold">{summary.total_critical_alerts}</p>
                <p className="text-sm text-gray-600">Total Critical Alerts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Organization cards */}
      {organizations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium">No organizations found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {organizations.map((org) => (
            <OrgCard key={org.organization_id} org={org} onSwitchOrg={handleSwitchOrg} />
          ))}
        </div>
      )}
    </div>
  );
}
